/**
 * Toast.jsx — Primitive de notification toast DA épurée (§62).
 *
 * §11/§18 — Primitive réutilisable du middleware UIX. Le helper
 * `src/utils/functions/notification.jsx` (Notification(msg, isSucess)) la rend
 * via `Store.addNotification({ content })` de react-notifications-component.
 * Tous les ~50 appelants existants de Notification() en bénéficient sans changement.
 *
 * §20 — tokens importés de ./theme (source unique). §12 ter — rem only.
 * §12 quinquies — carte 1rem, croix cerclée 50%. §62 — DA épurée :
 *   StatusDot + eyebrow mono + message plat + barre minuteur magenta.
 * §12 sexies — croix de fermeture cerclée. §64 — anglais.
 *
 * API :
 *   <Toast
 *     type="success" | "danger"   // défaut "success"
 *     title="Success"             // eyebrow (défaut "Success"/"Failed" selon type)
 *     message="…"                 // corps
 *     onClose={() => {}}          // fermeture (croix + auto-dismiss)
 *     duration={4000}             // ms avant auto-dismiss (barre minuteur)
 *   />
 */
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { COLORS, FONTS, RADII, media } from './theme';

const TYPE_COLOR = { success: COLORS.cta, danger: COLORS.danger };
const DEFAULT_TITLE = { success: 'Success', danger: 'Failed' };

const Card = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 21rem;
  max-width: calc(100vw - 1.5rem);
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.inkLine};
  border-radius: ${RADII.card};
  box-shadow: 0 0.5rem 1.5rem var(--ev-shadow, rgba(13, 10, 16, 0.1));
  padding: 0.875rem 0.875rem 1rem;
  overflow: hidden;
  cursor: default;
  ${media.sm`width: 100%;`}
`;

/* StatusDot inline (succès=cta, erreur=danger) — pas de chip rempli (§62-5) */
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.4rem;
  padding-right: 1.8rem;
`;
const Dot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: ${RADII.circle};
  flex-shrink: 0;
  background: ${(p) => TYPE_COLOR[p.$type] || COLORS.cta};
`;
const Eyebrow = styled.span`
  font-family: ${FONTS.mono};
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${(p) => TYPE_COLOR[p.$type] || COLORS.cta};
`;
const Message = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${COLORS.ink};
  word-break: break-word;
`;

/* Croix de fermeture cerclée (§12 sexies) en haut à droite */
const CloseBtn = styled.button`
  position: absolute;
  top: 0.7rem;
  right: 0.7rem;
  width: 1.4rem;
  height: 1.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.inkLine};
  border-radius: ${RADII.circle};
  color: ${COLORS.muted};
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  &:hover { color: ${COLORS.ink}; border-color: ${COLORS.ink}; }
`;

const shrink = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;
/* Barre minuteur magenta en bas (§62 accent sur une seule valeur clé) */
const TimerBar = styled.span`
  position: absolute;
  left: 0;
  bottom: 0;
  height: 0.1875rem;
  width: 100%;
  transform-origin: left center;
  background: ${COLORS.accent};
  border-bottom-left-radius: ${RADII.card};
  border-bottom-right-radius: ${RADII.card};
  animation: ${shrink} ${(p) => p.$duration}ms linear forwards;
`;

export const Toast = ({
  type = 'success',
  title,
  message,
  onClose,
  duration = 4000,
}) => {
  const t = TYPE_COLOR[type] ? type : 'success';
  const eyebrow = title || DEFAULT_TITLE[t];
  const timerRef = useRef(null);

  useEffect(() => {
    if (!onClose || !duration) return undefined;
    timerRef.current = setTimeout(onClose, duration);
    return () => clearTimeout(timerRef.current);
  }, [onClose, duration]);

  return (
    <Card role="status" aria-live="polite">
      <Head>
        <Dot $type={t} />
        <Eyebrow $type={t}>{eyebrow}</Eyebrow>
      </Head>
      <Message>{message}</Message>
      {onClose && (
        <CloseBtn type="button" aria-label="Close" onClick={onClose}>
          &times;
        </CloseBtn>
      )}
      {duration ? <TimerBar $duration={duration} /> : null}
    </Card>
  );
};

export default Toast;
